import React, { useState, useEffect, Fragment, useCallback } from 'react';
import { isFormValid } from './form.validator';
import { IFORM, IFORM_ELEMENT } from './formControl';
import _isEqual from 'lodash.isequal';
import debounce from 'lodash.debounce';

type Props = {
  formConfig: IFORM;
  disabled?: boolean;
  className?: string;
  onSubmit: Function;
  onChange?: Function;
  onFieldBlurFn?: Function;
};

export const FormBuilder: React.FC<Props> = ({ onFieldBlurFn, formConfig, disabled, className, onSubmit, onChange: callParentOnChange }: Props) => {
  const [form, setForm] = useState<IFORM>(formConfig);
  const cssClass = className || 'flg-lg-6 flg-md-6';
  /**
   *  In Firefox, lodash debounced functions will wait for the assigned timeout and the timer resets if the debounced function is called again.
   *  However, for Chrome, nothing works as expected: the debounced function calls the inner function as soon as called and will not wait for resets in the timer.
   *
   * After createLead in OrderDetailsAccordion, we set form state after promise success. Parallely, lead fetch is going on, which triggers useEffect(() => {}, [getCurrentLead()?.leadId]).
   * This in turn resets timer in firefox but, works fine in Chrome.
   */

  const updateOnFormChange = useCallback(debounce(onSubmit, 1000), []);

  const disableAll = (flag: boolean) => {
    const updatedForm = Object.keys(form).reduce((acc: IFORM, key) => {
      acc[key] = { ...form[key], disabled: flag };
      return acc;
    }, {});
    setForm(updatedForm);
  };

  const clearAll = () => {
    const updatedForm = Object.keys(form).reduce((acc: IFORM, key) => {
      acc[key] = { ...form[key], value: '' };
      return acc;
    }, {});
    setForm(updatedForm);
  };

  const onChange = (key: string, value: string | object, field: IFORM_ELEMENT) => {
    const oldValue = form[key]?.value;
    const updatedForm = {
      ...form,
      [key]: {
        ...form[key],
        value,
        touched: true,
        errors: form[key]?.validator?.(value)
      }
    };
    setForm(updatedForm);
    callParentOnChange?.(key, value, field, { form: updatedForm, setForm, disableAll, oldValue, newValue: value, clearAll });
  };

  const onFieldBlur = (key: string, field: IFORM_ELEMENT) => {
    const { value, validator, errors: existingError = [] } = form[key];
    let errors = [];
    if (typeof validator === 'function') {
      errors = validator(value);
    }
    setForm({
      ...form,
      [key]: {
        ...form[key],
        touched: true,
        errors: errors.concat(existingError)
      }
    });
    onFieldBlurFn?.(form, field);
  };

  useEffect(() => {
    updateOnFormChange({ isValid: isFormValid(form), values: form, form, setForm, disableAll, clearAll });
  }, [form]);

  useEffect(() => {
    if (!_isEqual(form, formConfig)) {
      setForm({ ...form, ...formConfig });
    }
  }, [formConfig]);

  const renderForm = (form: IFORM) => {
    const fields: Array<string> = Object.keys(form);
    return fields.map((key: string) => {
      const field = form[key];
      if (field?.hide) {
        return null;
      }
      const DynamicElement = field?.element?.type || <> </>;
      return (
        <div key={key} className={`${field.className || cssClass} ${disabled ? 'disabled' : ''}`}>
          <Fragment>
            <DynamicElement
              key={key}
              label={field?.label}
              disabled={field?.disabled}
              onBlur={() => onFieldBlur(key, field)}
              required
              errors={field.errors}
              touched={field.touched}
              value={field.value}
              onChange={(value: any) => onChange(key, value, field)}
              {...field.element.props}
            />
            {field?.loader && <span className="loading_icon form_loader"></span>}
            {field.key === 'pincode' && field?.loader && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  background: 'rgba(0,0,0,0.4)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 9999
                }}
              >
                <style>
                  {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
                </style>
                <div
                  style={{
                    width: '50px',
                    height: '50px',
                    border: '4px solid #ca1616',
                    borderTop: '6px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '15px'
                  }}
                />
                <div style={{ fontSize: '21px' }}>Fetching Cities...</div>
              </div>
            )}
          </Fragment>
          {field?.fieldMessage ? <p className="field-message">{field?.fieldMessage}</p> : null}
        </div>
      );
    });
  };

  return <Fragment> {renderForm(form)} </Fragment>;
};

export type { IFORM_ELEMENT, IFORM } from './formControl';
